import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Wallet, Calculator, FileText, HelpCircle } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './FreelanceTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './FreelanceTaxCalculatorPage/components/ResultsOverview';
import PaymentsSchedule from './FreelanceTaxCalculatorPage/components/PaymentsSchedule';
import QuickAdjustmentSidebar from './FreelanceTaxCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonsWidget from './FreelanceTaxCalculatorPage/components/ComparisonsWidget';
import TipsSidebar from './FreelanceTaxCalculatorPage/components/TipsSidebar';
import InfoContent from './FreelanceTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  incomeType: 'general', // Тип дохода: general (стандартные 4%), ip_insurance (6% для ИП)
  incomeAmount: '100000', // Сумма дохода
  incomeSource: 'russia', // Источник дохода: russia, foreign
  incomePeriod: 'month', // Период дохода: month, quarter, year, custom
  customPeriod: {
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
  },
  hasExpenses: false, // Учитывать расходы
  expenses: '0', // Сумма расходов
  expenseItems: [], // Детализация расходов
  hasDeductions: false, // Учитывать налоговые вычеты
  deductions: '0', // Сумма налоговых вычетов
  hasChildrenDeduction: false, // Вычет на детей
  childrenCount: 0, // Количество детей
  monthlyIncome: [], // Доход по месяцам для расчета помесячной налоговой нагрузки
  incomeGrowthRate: 0, // Процент роста дохода (для прогноза)
  includeInsurance: false, // Учитывать добровольные взносы на пенсионное страхование
  insuranceAmount: '0', // Сумма страховых взносов
  includeSocialBenefits: false, // Учитывать социальные льготы
  region: 'Moscow', // Регион для региональных льгот
  hasOtherIncome: false, // Есть другие источники дохода (НДФЛ)
  otherIncome: '0', // Сумма дохода, облагаемая НДФЛ
  fiscalRole: 'individual', // Статус: individual (физлицо), entrepreneur (ИП)
  hasActiveIP: false, // Действующий ИП (для учета отчислений и вычетов)
  ipRegistrationYear: new Date().getFullYear(), // Год регистрации ИП
  taxSystem: 'npd', // Система налогообложения: npd (НПД), usn_income (УСН доходы), usn_profit (УСН доходы-расходы)
  adjustForInflation: false, // Учитывать инфляцию в прогнозах
};

// Определение типов периодов
export const periodTypes = [
  { id: 'month', name: 'Месяц' },
  { id: 'quarter', name: 'Квартал' },
  { id: 'year', name: 'Год' },
  { id: 'custom', name: 'Произвольный период' }
];

// Определение статусов
export const fiscalRoles = [
  { id: 'individual', name: 'Физическое лицо' },
  { id: 'entrepreneur', name: 'Индивидуальный предприниматель' }
];

// Определение режимов налогообложения
export const taxSystems = [
  { id: 'npd', name: 'Налог на профессиональный доход (самозанятость)', rate: '4/6%' },
  { id: 'usn_income', name: 'УСН Доходы', rate: '6%' },
  { id: 'usn_profit', name: 'УСН Доходы минус расходы', rate: '15%' }
];

// Регионы России (сокращенный список)
export const russianRegions = [
  { id: 'Moscow', name: 'Москва' },
  { id: 'SaintPetersburg', name: 'Санкт-Петербург' },
  { id: 'MoscowRegion', name: 'Московская область' },
  { id: 'Tatarstan', name: 'Республика Татарстан' },
  { id: 'Crimea', name: 'Республика Крым' },
  { id: 'Kaliningrad', name: 'Калининградская область' },
  { id: 'Other', name: 'Другой регион' }
];

/**
 * FreelanceTaxCalculatorPage Component - Калькулятор налогов для самозанятых и ИП
 */
const FreelanceTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => {
    // В реальном проекте это будет приходить из getToolById
    return {
      id: 'freelance-tax-calculator',
      name: 'Калькулятор налогов для самозанятых',
      metaTitle: 'Калькулятор налогов для самозанятых | Gensio',
      metaDescription: 'Полный расчет налогов для самозанятых в России: расчет НПД (налога на профессиональный доход), формирование графика платежей, сравнение с другими налоговыми режимами, учёт вычетов и льгот.',
      relatedTools: ['vat-calculator', 'loan-calculator', 'deposit-calculator'],
      subTools: [
        { id: 'individual', name: 'Для физических лиц', path: '/tools/finance/freelance-tax-calculator/individual' },
        { id: 'entrepreneur', name: 'Для ИП', path: '/tools/finance/freelance-tax-calculator/entrepreneur' },
        { id: 'comparison', name: 'Сравнение режимов', path: '/tools/finance/freelance-tax-calculator/comparison' },
      ]
    };
  }, []);
  const pageTitle = tool?.metaTitle || 'Калькулятор налогов для самозанятых | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный расчет налогов для самозанятых в России: расчет НПД (налога на профессиональный доход), формирование графика платежей, сравнение с другими налоговыми режимами, учёт вычетов и льгот.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedTaxCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved tax calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'individual':
          newState.fiscalRole = 'individual';
          newState.taxSystem = 'npd';
          break;
        case 'entrepreneur':
          newState.fiscalRole = 'entrepreneur';
          newState.hasActiveIP = true;
          newState.taxSystem = 'npd';
          break;
        case 'comparison':
          // Настройка для сравнения разных режимов
          newState = { ...prev };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчёт НПД: ${formData.incomeAmount}₽`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      taxSystem: formData.taxSystem
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // Перерасчет графика платежей
      if (calculationToLoad.results && calculationToLoad.results.schedule) {
        setSchedule(calculationToLoad.results.schedule);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор налогов для самозанятых'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              periodTypes={periodTypes}
              fiscalRoles={fiscalRoles}
              taxSystems={taxSystems}
              russianRegions={russianRegions}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <PaymentsSchedule 
                  schedule={schedule} 
                  results={results}
                  formData={formData}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о налогах самозанятых --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Виджет сравнения налоговых режимов --- */}
            <ComparisonsWidget 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default FreelanceTaxCalculatorPage;